import React, { useEffect, useRef, useState } from 'react';

import {
  SafetyAvailability,
  SafetyReportCategory,
  getSafetyReportAvailability,
  submitSafetyReport,
} from '../api/safety-reports';

/**
 * UC-S09 STU-11-FR-08 + MC-09 v1-1 — the learner's "something was not OK" action.
 *
 * For the learner session and the kid shell to place where a child can reach
 * it. It draws nothing at all unless the server says the intake is on for this
 * learner — which it does only when the `safety_reports` flag is on, the nine
 * MC-09 activation conditions have evidence (a named titular and suplente, a
 * preventive measure that can cut a learner's access, a verified per-country
 * route, an approved drill…) and the learner is inside the declared age range
 * (MC09-R15). A failed availability check also draws nothing. MC-09 specifies
 * no alternative for the switched-off state, so none is invented here.
 *
 * The copy promises only what is true:
 *  - no reason and no words are required to be heard (MC09-R02, AC06);
 *  - "Recibimos" appears only after the server confirmed the report was stored,
 *    and nothing says anyone has read it or will answer (MC09-R03, AC09);
 *  - no absolute secrecy: what the child says may be shared with whoever needs
 *    to help protect them (MC09-R10);
 *  - immediate danger: do not wait for the app; use the verified local text
 *    when the server has one, never a number from another country (§5, AC08);
 *  - a failed send is shown honestly, with the attended alternative Ops
 *    configured (MC09-AC05).
 * An answer that never arrived is shown as unknown, and the retry reuses the
 * same request key, so trying again can never file it twice (MC09-R07).
 */

interface Props {
  studentId: string;
  enrollmentId?: string;
  /** The session the learner is in, when there is one. */
  seq?: number;
}

const CATEGORIES: Array<{ value: SafetyReportCategory; label: string }> = [
  { value: 'FELT_UNSAFE', label: 'Me sentí inseguro o con miedo' },
  { value: 'INAPPROPRIATE_CONTENT', label: 'Dijeron o mostraron algo que no está bien' },
  {
    value: 'PRIVATE_CONTACT_REQUEST',
    label: 'Me pidieron datos, fotos o hablar fuera de la clase',
  },
  { value: 'BULLYING', label: 'Alguien me molestó o me trató mal' },
  { value: 'OTHER', label: 'Otra cosa' },
];

const MAX = 2000;

function newRequestKey(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

type Phase = 'closed' | 'open' | 'sending' | 'received' | 'unknown' | 'failed';

const SafetyReportAction: React.FC<Props> = ({ studentId, enrollmentId, seq }) => {
  const [availability, setAvailability] = useState<SafetyAvailability>({ enabled: false });
  const [phase, setPhase] = useState<Phase>('closed');
  const [category, setCategory] = useState<SafetyReportCategory | null>(null);
  const [description, setDescription] = useState('');
  const requestKey = useRef<string>('');

  useEffect(() => {
    let cancelled = false;
    getSafetyReportAvailability(studentId)
      .then((r) => !cancelled && setAvailability(r?.enabled === true ? r : { enabled: false }))
      // Unconfirmed availability draws nothing, by design (see above).
      .catch(() => !cancelled && setAvailability({ enabled: false }));
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  if (!availability.enabled) return null;

  const open = () => {
    requestKey.current = newRequestKey();
    setCategory(null);
    setDescription('');
    setPhase('open');
  };

  const send = async () => {
    setPhase('sending');
    try {
      const text = description.trim();
      const receipt = await submitSafetyReport({
        studentId,
        enrollmentId,
        seq,
        ...(category ? { category } : {}),
        ...(text ? { description: text } : {}),
        requestKey: requestKey.current,
      });
      setPhase(receipt?.status === 'RECEIVED' ? 'received' : 'unknown');
    } catch (e: any) {
      const status = typeof e?.status === 'number' ? e.status : undefined;
      // A 4xx is a definite refusal; anything else (no answer, a 5xx) is unknown.
      setPhase(status !== undefined && status >= 400 && status < 500 ? 'failed' : 'unknown');
    }
  };

  const danger = (
    <p>
      Si estás en peligro ahora, no esperes la respuesta de la app.{' '}
      {availability.urgentGuidance ?? 'Busca a un adulto de confianza cerca de ti.'}
    </p>
  );
  const alternative = availability.alternativeChannel ? (
    <p>{availability.alternativeChannel}</p>
  ) : null;

  if (phase === 'closed') {
    return (
      <button type="button" className="btn btn-o" onClick={open}>
        Contar algo que no estuvo bien
      </button>
    );
  }

  return (
    <section aria-labelledby="safety-report-title" style={{ maxWidth: 520 }}>
      <h2 id="safety-report-title" style={{ fontSize: 18 }}>
        ¿Pasó algo que no estuvo bien?
      </h2>
      {availability.drill ? <p className="mut">Modo simulacro: esto es una prueba.</p> : null}

      {phase === 'received' ? (
        <>
          <p role="status">
            Recibimos tu mensaje. Lo guardamos para el equipo de seguridad de IQMaxx.
          </p>
          {danger}
          <button type="button" className="btn btn-o" onClick={() => setPhase('closed')}>
            Cerrar
          </button>
        </>
      ) : phase === 'unknown' ? (
        <>
          <p role="alert">No sabemos si tu mensaje llegó. Puedes intentarlo otra vez.</p>
          {alternative}
          <button type="button" className="btn btn-p" onClick={send}>
            Intentar otra vez
          </button>{' '}
          <button type="button" className="btn btn-o" onClick={() => setPhase('closed')}>
            Ahora no
          </button>
        </>
      ) : phase === 'failed' ? (
        <>
          <p role="alert">No se pudo enviar. Pide ayuda a un adulto de confianza.</p>
          {alternative}
          <button type="button" className="btn btn-o" onClick={() => setPhase('closed')}>
            Cerrar
          </button>
        </>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
        >
          <p>
            Cuéntanos qué pasó, si quieres. No necesitas pruebas ni explicar mucho. Tu profesor no
            verá este mensaje.
          </p>
          <p>
            Lo que nos cuentes puede compartirse con las personas que necesiten ayudar a protegerte.
          </p>
          <fieldset style={{ border: 0, padding: 0 }}>
            <legend>¿Qué pasó? (puedes no elegir)</legend>
            {CATEGORIES.map((c) => (
              <label key={c.value} style={{ display: 'block', margin: '6px 0' }}>
                <input
                  type="radio"
                  name="safety-category"
                  value={c.value}
                  checked={category === c.value}
                  onChange={() => setCategory(c.value)}
                />{' '}
                {c.label}
              </label>
            ))}
          </fieldset>
          <label htmlFor="safety-description" style={{ display: 'block', marginTop: 8 }}>
            Escríbelo con tus palabras
          </label>
          <textarea
            id="safety-description"
            className="inp"
            rows={4}
            maxLength={MAX}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ width: '100%' }}
          />
          {danger}
          <button type="submit" className="btn btn-p" disabled={phase === 'sending'}>
            {phase === 'sending' ? 'Enviando…' : 'Enviar'}
          </button>{' '}
          <button type="button" className="btn btn-o" onClick={() => setPhase('closed')}>
            Ahora no
          </button>
        </form>
      )}
    </section>
  );
};

export default SafetyReportAction;
